import React, { useState, useEffect, useCallback } from 'react';
import { supportAdminAPI } from '../../services/api';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';

interface TicketMessage {
  id: number;
  sender_name: string;
  message: string;
  is_internal: boolean;
  created_at: string;
}

interface Ticket {
  id: number;
  customer_name: string;
  created_by_name: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  admin_notes: string;
  assigned_to: number | null;
  assigned_to_name: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  created_at: string;
  error_data: any;
  messages: TicketMessage[];
}

interface Stats {
  open_count: number;
  in_progress_count: number;
  new_today: number;
  avg_resolution_hours: number | null;
}

const statusColors: Record<string, { bg: string; color: string }> = {
  open: { bg: '#e3f2fd', color: '#1565c0' },
  in_progress: { bg: '#fff8e1', color: '#f57f17' },
  resolved: { bg: '#e8f5e9', color: '#2e7d32' },
  closed: { bg: '#f5f5f5', color: '#757575' },
};

const priorityColors: Record<string, { bg: string; color: string }> = {
  low: { bg: '#f5f5f5', color: '#757575' },
  medium: { bg: '#fff8e1', color: '#f57f17' },
  high: { bg: '#fff3e0', color: '#e65100' },
  critical: { bg: '#fdecea', color: '#c62828' },
};

const SupportTicketsAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tickets'>('dashboard');
  const [stats, setStats] = useState<Stats | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedTicket, setExpandedTicket] = useState<Ticket | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  // Server search: input binds to `searchInput` (instant); debounced `search` (400ms)
  // drives loadTickets so typing fires one request after the pause.
  const { value: searchInput, setValue: setSearchInput, debounced: search } = useDebouncedSearch('', 400);

  // Reply
  const [replyText, setReplyText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);

  const loadStats = async () => {
    try {
      const res = await supportAdminAPI.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (search) params.search = search;
      const res = await supportAdminAPI.getTickets(params);
      setTickets(res.data);
    } catch (err) {
      console.error('Failed to load tickets', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, categoryFilter, search]);

  useEffect(() => {
    loadStats();
    loadTickets();
  }, [loadTickets]);

  const handleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedTicket(null);
      return;
    }
    try {
      const res = await supportAdminAPI.getTicket(id);
      setExpandedTicket(res.data);
      setExpandedId(id);
      setReplyText('');
      setIsInternal(false);
    } catch (err) {
      console.error('Failed to load ticket', err);
    }
  };

  const handleUpdate = async (field: string, value: any) => {
    if (!expandedTicket) return;
    try {
      const res = await supportAdminAPI.updateTicket(expandedTicket.id, { [field]: value });
      setExpandedTicket(res.data);
      loadTickets();
      if (field === 'status') loadStats();
    } catch (err) {
      console.error('Failed to update ticket', err);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !expandedTicket) return;
    setSendingReply(true);
    try {
      await supportAdminAPI.addMessage(expandedTicket.id, {
        message: replyText.trim(),
        is_internal: isInternal,
      });
      setReplyText('');
      setIsInternal(false);
      const res = await supportAdminAPI.getTicket(expandedTicket.id);
      setExpandedTicket(res.data);
    } catch (err) {
      console.error('Failed to send reply', err);
    } finally {
      setSendingReply(false);
    }
  };

  const tabStyle = (tab: string): React.CSSProperties => ({
    padding: '10px 20px', border: 'none', borderBottom: activeTab === tab ? '3px solid #1976d2' : '3px solid transparent',
    background: 'none', color: activeTab === tab ? '#1976d2' : '#666',
    fontWeight: activeTab === tab ? 700 : 400, fontSize: 14, cursor: 'pointer',
  });

  const selectStyle: React.CSSProperties = {
    padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13,
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Support Tickets</h1>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 24 }}>
        <button style={tabStyle('dashboard')} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
        <button style={tabStyle('tickets')} onClick={() => setActiveTab('tickets')}>All Tickets</button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <StatCard label="Open" value={stats.open_count} color="#1565c0" bg="#e3f2fd" />
          <StatCard label="In Progress" value={stats.in_progress_count} color="#f57f17" bg="#fff8e1" />
          <StatCard label="New Today" value={stats.new_today} color="#2e7d32" bg="#e8f5e9" />
          <StatCard
            label="Avg Resolution"
            value={stats.avg_resolution_hours != null ? `${stats.avg_resolution_hours}h` : 'N/A'}
            color="#6a1b9a"
            bg="#f3e5f5"
          />
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} style={selectStyle}>
              <option value="">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={selectStyle}>
              <option value="">All Categories</option>
              <option value="bug">Bug</option>
              <option value="feature_request">Feature Request</option>
              <option value="question">Question</option>
              <option value="billing">Billing</option>
              <option value="other">Other</option>
            </select>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search tickets..."
              style={{ ...selectStyle, minWidth: 180 }}
            />
          </div>

          {loading && tickets.length === 0 ? (
            <p style={{ color: '#888' }}>Loading...</p>
          ) : tickets.length === 0 ? (
            <p style={{ color: '#888' }}>No tickets found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Table header */}
              <div style={{
                display: 'grid', gridTemplateColumns: '50px 1fr 150px 90px 80px 90px 100px',
                padding: '8px 12px', background: '#f5f5f5', fontSize: 12, fontWeight: 600, color: '#666',
                borderRadius: '6px 6px 0 0',
              }}>
                <span>#</span><span>Subject</span><span>Customer</span>
                <span>Status</span><span>Priority</span><span>Category</span><span>Created</span>
              </div>

              {tickets.map(t => {
                const sc = statusColors[t.status] || statusColors.open;
                const pc = priorityColors[t.priority] || priorityColors.medium;
                const isExpanded = expandedId === t.id;

                return (
                  <React.Fragment key={t.id}>
                    <div
                      onClick={() => handleExpand(t.id)}
                      style={{
                        display: 'grid', gridTemplateColumns: '50px 1fr 150px 90px 80px 90px 100px',
                        padding: '10px 12px', background: isExpanded ? '#f0f7ff' : '#fff',
                        borderBottom: '1px solid #eee', cursor: 'pointer', fontSize: 13,
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ color: '#888' }}>{t.id}</span>
                      <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.subject}
                      </span>
                      <span style={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.customer_name}</span>
                      <span>
                        <span style={{
                          background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 600,
                          padding: '2px 6px', borderRadius: 8,
                        }}>
                          {t.status.replace('_', ' ')}
                        </span>
                      </span>
                      <span>
                        <span style={{
                          background: pc.bg, color: pc.color, fontSize: 11, fontWeight: 600,
                          padding: '2px 6px', borderRadius: 8,
                        }}>
                          {t.priority}
                        </span>
                      </span>
                      <span style={{ color: '#666', fontSize: 12 }}>{t.category.replace('_', ' ')}</span>
                      <span style={{ color: '#888', fontSize: 12 }}>{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Expanded detail panel */}
                    {isExpanded && expandedTicket && (
                      <div style={{
                        background: '#fafbfc', border: '1px solid #e0e0e0', borderTop: 'none',
                        padding: 20, marginBottom: 4,
                      }}>
                        {/* Ticket info */}
                        <div style={{ marginBottom: 16 }}>
                          <p style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {expandedTicket.description}
                          </p>
                          {expandedTicket.error_data && (
                            <details style={{ marginTop: 8 }}>
                              <summary style={{ cursor: 'pointer', fontSize: 13, color: '#c62828' }}>Error Data</summary>
                              <pre style={{
                                background: '#fff', padding: 10, borderRadius: 4, fontSize: 12,
                                overflow: 'auto', maxHeight: 200, border: '1px solid #eee',
                              }}>
                                {JSON.stringify(expandedTicket.error_data, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>

                        {/* Controls */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                          <div>
                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Status</label>
                            <select
                              value={expandedTicket.status}
                              onChange={e => handleUpdate('status', e.target.value)}
                              style={selectStyle}
                            >
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Priority</label>
                            <select
                              value={expandedTicket.priority}
                              onChange={e => handleUpdate('priority', e.target.value)}
                              style={selectStyle}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="critical">Critical</option>
                            </select>
                          </div>
                        </div>

                        {/* Admin Notes */}
                        <div style={{ marginBottom: 20 }}>
                          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Admin Notes</label>
                          <textarea
                            value={expandedTicket.admin_notes}
                            onChange={e => setExpandedTicket({ ...expandedTicket, admin_notes: e.target.value })}
                            onBlur={() => handleUpdate('admin_notes', expandedTicket.admin_notes)}
                            rows={2}
                            style={{
                              width: '100%', padding: '8px 10px', border: '1px solid #ccc',
                              borderRadius: 4, fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        {/* Messages */}
                        <div style={{ marginBottom: 16 }}>
                          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#333' }}>Messages</h4>
                          {expandedTicket.messages.length === 0 ? (
                            <p style={{ color: '#888', fontSize: 13 }}>No messages yet.</p>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {expandedTicket.messages.map(msg => (
                                <div key={msg.id} style={{
                                  background: msg.is_internal ? '#fffde7' : '#fff',
                                  border: `1px solid ${msg.is_internal ? '#fff176' : '#e0e0e0'}`,
                                  borderRadius: 6, padding: 10,
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontWeight: 600, fontSize: 12 }}>
                                      {msg.sender_name}
                                      {msg.is_internal && (
                                        <span style={{ color: '#f57f17', marginLeft: 6, fontWeight: 400 }}>(Internal Note)</span>
                                      )}
                                    </span>
                                    <span style={{ color: '#aaa', fontSize: 11 }}>
                                      {new Date(msg.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                  <p style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Reply box */}
                        <div>
                          <textarea
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            placeholder={isInternal ? 'Type an internal note...' : 'Type a reply to customer...'}
                            rows={2}
                            style={{
                              width: '100%', padding: '8px 10px', border: '1px solid #ccc',
                              borderRadius: 4, fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
                              marginBottom: 8,
                            }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <button
                              onClick={handleSendReply}
                              disabled={sendingReply || !replyText.trim()}
                              style={{
                                background: sendingReply || !replyText.trim() ? '#90caf9' : '#1976d2',
                                color: '#fff', border: 'none', borderRadius: 4,
                                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                                cursor: sendingReply || !replyText.trim() ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {sendingReply ? 'Sending...' : 'Send'}
                            </button>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#666', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={isInternal}
                                onChange={e => setIsInternal(e.target.checked)}
                              />
                              Internal Note
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number | string; color: string; bg: string }> = ({ label, value, color, bg }) => (
  <div style={{
    background: bg, borderRadius: 10, padding: 20, textAlign: 'center',
  }}>
    <div style={{ fontSize: 32, fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: 13, color, marginTop: 4, fontWeight: 500 }}>{label}</div>
  </div>
);

export default SupportTicketsAdmin;
